import React, { useEffect, useState } from "react";
import { api, money } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Plus } from "lucide-react";
import { toast } from "sonner";

const err = (e) => toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Request failed");

function Pick({ value, onChange, options, testid, className = "" }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={`h-9 ${className}`} data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
}
const BLANK = { name: "", price: 0, currency: "INR", bidz: 1, validity_days: 90, active: true, order: 10, description: "", max_purchases_per_user: 0, min_account_age_days: 0 };
const PKG_FIELDS = [["price", "Price"], ["bidz", "Bidz"], ["validity_days", "Validity (days)"], ["order", "Order"], ["max_purchases_per_user", "Max buys/user (0=∞)"], ["min_account_age_days", "Min account age (days)"]];

function PackageRow({ p, onSaved }) {
  const [f, setF] = useState(p);
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const save = async () => {
    const body = { ...f };
    delete body.kind;
    PKG_FIELDS.forEach(([k]) => { body[k] = Number(body[k]); });
    try {
      if (p.id) await api.put(`/admin/bidz/packages/${p.id}`, body); else await api.post("/admin/bidz/packages", body);
      toast.success("Package saved"); onSaved();
    } catch (e) { err(e); }
  };
  const tid = p.code || "new";
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-4 space-y-3" data-testid={`admin-bidz-pkg-${tid}`}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <label className="text-xs col-span-2">Name<Input value={f.name} onChange={e => set("name", e.target.value)} data-testid={`admin-bidz-pkg-${tid}-name`} /></label>
        <label className="text-xs">Currency<Input value={f.currency} onChange={e => set("currency", e.target.value.toUpperCase())} /></label>
        {PKG_FIELDS.map(([k, l]) => (
          <label key={k} className="text-xs">{l}<Input type="number" value={f[k]} onChange={e => set(k, e.target.value)} data-testid={`admin-bidz-pkg-${tid}-${k}`} /></label>
        ))}
        <label className="text-xs col-span-2">Description<Input value={f.description || ""} onChange={e => set("description", e.target.value)} /></label>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm"><Switch checked={f.active} onCheckedChange={v => set("active", v)} data-testid={`admin-bidz-pkg-${tid}-active`} /> {f.active ? "Active" : "Hidden"}
          <span className="text-xs text-slate-500 ml-3">{Number(f.bidz) > 0 ? `${money(Number(f.price) / Number(f.bidz), f.currency)} per Bidz` : ""}</span>
        </div>
        <Button size="sm" onClick={save} data-testid={`admin-bidz-pkg-${tid}-save`} className="bg-slate-900 text-white">Save</Button>
      </div>
    </div>
  );
}

function NumRows({ rows, valueKey, valueLabel, prefix, onChange }) {
  return rows.map((t, i) => (
    <div key={`${prefix}-${i}`} className="grid grid-cols-2 gap-3">
      <label className="text-xs">Reserve up to (₹)<Input type="number" value={t.max_value} onChange={e => onChange(i, "max_value", e.target.value)} data-testid={`admin-${prefix}-${i}-max`} /></label>
      <label className="text-xs">{valueLabel}<Input type="number" value={t[valueKey]} onChange={e => onChange(i, valueKey, e.target.value)} data-testid={`admin-${prefix}-${i}-${valueKey}`} /></label>
    </div>
  ));
}

function RulesCard() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/settings").then(r => setS(r.data)); }, []);
  if (!s) return null;
  const setTier = (i, k, v) => setS(x => ({ ...x, bidz_tiers: x.bidz_tiers.map((t, j) => (j === i ? { ...t, [k]: v } : t)) }));
  const setBand = (i, k, v) => setS(x => ({ ...x, pass_bands: x.pass_bands.map((t, j) => (j === i ? { ...t, [k]: v } : t)) }));
  const save = async () => {
    try {
      await api.put("/settings", {
        bidz_enabled: s.bidz_enabled,
        bidz_tiers: s.bidz_tiers.map(t => ({ max_value: Number(t.max_value), bidz: Number(t.bidz) })),
        bidz_above_max: Number(s.bidz_above_max),
        bidz_restore_validity_days: Number(s.bidz_restore_validity_days),
        bidz_promo_enabled: s.bidz_promo_enabled,
        bidz_promo_amount: Number(s.bidz_promo_amount),
        bidz_promo_validity_days: Number(s.bidz_promo_validity_days),
        pass_enabled: s.pass_enabled,
        pass_bands: s.pass_bands.map(t => ({ max_value: Number(t.max_value), price: Number(t.price) })),
        pass_above_price: Number(s.pass_above_price),
        bidz_alerts_enabled: s.bidz_alerts_enabled,
        bidz_low_threshold: Number(s.bidz_low_threshold),
        bidz_expiry_reminder_days: String(s.bidz_expiry_reminder_days).split(",").map(x => Number(x.trim())).filter(x => x > 0),
      });
      toast.success("Rules saved", { description: "New Bidz costs and pass prices apply to auctions that go live from now on." });
    } catch (e) { err(e); }
  };
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 space-y-4" data-testid="admin-bidz-rules">
      <div className="flex items-center justify-between"><h3 className="text-lg font-bold">Bidz cost tiers & rules</h3>
        <div className="flex items-center gap-2 text-sm"><Switch checked={s.bidz_enabled} onCheckedChange={v => setS({ ...s, bidz_enabled: v })} data-testid="admin-bidz-enabled" /> Bidz required to bid</div>
      </div>
      <div className="space-y-2">
        <NumRows rows={s.bidz_tiers} valueKey="bidz" valueLabel="Bidz per bid" prefix="bidz-tier" onChange={setTier} />
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs">Above highest tier — Bidz per bid<Input type="number" value={s.bidz_above_max} onChange={e => setS({ ...s, bidz_above_max: e.target.value })} data-testid="admin-bidz-above-max" /></label>
          <label className="text-xs">Restored / replacement Bidz validity (days)<Input type="number" value={s.bidz_restore_validity_days} onChange={e => setS({ ...s, bidz_restore_validity_days: e.target.value })} data-testid="admin-bidz-restore-days" /></label>
        </div>
      </div>
      <div className="rounded-xl bg-slate-50 dark:bg-slate-800 p-4 space-y-3" data-testid="admin-pass-rules">
        <div className="flex items-center gap-2 text-sm font-semibold"><Switch checked={s.pass_enabled} onCheckedChange={v => setS({ ...s, pass_enabled: v })} data-testid="admin-pass-enabled" /> One-Time Auction Pass (pay per auction, no Bidz)</div>
        <NumRows rows={s.pass_bands} valueKey="price" valueLabel="Pass price (₹)" prefix="pass-band" onChange={setBand} />
        <label className="text-xs block">Above highest band — pass price (₹)<Input type="number" value={s.pass_above_price} onChange={e => setS({ ...s, pass_above_price: e.target.value })} data-testid="admin-pass-above-price" /></label>
      </div>
      <div className="rounded-xl bg-slate-50 dark:bg-slate-800 p-4 space-y-3" data-testid="admin-bidz-alerts">
        <div className="flex items-center gap-2 text-sm font-semibold"><Switch checked={s.bidz_alerts_enabled} onCheckedChange={v => setS({ ...s, bidz_alerts_enabled: v })} data-testid="admin-bidz-alerts-enabled" /> Low-balance & expiry alerts</div>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs">Alert when wallet ≤ (Bidz)<Input type="number" value={s.bidz_low_threshold} onChange={e => setS({ ...s, bidz_low_threshold: e.target.value })} data-testid="admin-bidz-low-threshold" /></label>
          <label className="text-xs">Expiry reminders (days before, comma-separated)<Input value={String(s.bidz_expiry_reminder_days)} onChange={e => setS({ ...s, bidz_expiry_reminder_days: e.target.value })} data-testid="admin-bidz-reminder-days" /></label>
        </div>
      </div>
      <div className="rounded-xl bg-slate-50 dark:bg-slate-800 p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm font-semibold"><Switch checked={s.bidz_promo_enabled} onCheckedChange={v => setS({ ...s, bidz_promo_enabled: v })} data-testid="admin-bidz-promo-enabled" /> Promotional Bidz for new sign-ups</div>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs">Promo Bidz<Input type="number" value={s.bidz_promo_amount} onChange={e => setS({ ...s, bidz_promo_amount: e.target.value })} data-testid="admin-bidz-promo-amount" /></label>
          <label className="text-xs">Promo validity (days)<Input type="number" value={s.bidz_promo_validity_days} onChange={e => setS({ ...s, bidz_promo_validity_days: e.target.value })} /></label>
        </div>
      </div>
      <Button onClick={save} data-testid="admin-bidz-rules-save" className="bg-slate-900 text-white">Save rules</Button>
    </div>
  );
}

function ActionsCard({ onDone }) {
  const [g, setG] = useState({ email: "", bidz: 10, validity_days: 90, reason: "", kind: "promo" });
  const [r, setR] = useState({ listing_id: "", reason: "" });
  const grant = async () => {
    try { await api.post("/admin/bidz/grant", { ...g, bidz: Number(g.bidz), validity_days: Number(g.validity_days) }); toast.success("Bidz granted"); onDone(); } catch (e) { err(e); }
  };
  const restore = async () => {
    try { const x = await api.post(`/admin/bidz/restore-listing/${r.listing_id.trim()}`, { reason: r.reason }); toast.success(`${x.data.bidz_restored} Bidz restored to ${x.data.users} users`); onDone(); } catch (e) { err(e); }
  };
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 space-y-3" data-testid="admin-bidz-grant">
        <h3 className="font-bold">Grant Bidz (promo or restoration)</h3>
        <Input placeholder="User email" value={g.email} onChange={e => setG({ ...g, email: e.target.value })} data-testid="admin-bidz-grant-email" />
        <div className="grid grid-cols-3 gap-2">
          <Input type="number" value={g.bidz} onChange={e => setG({ ...g, bidz: e.target.value })} data-testid="admin-bidz-grant-amount" />
          <Input type="number" value={g.validity_days} onChange={e => setG({ ...g, validity_days: e.target.value })} data-testid="admin-bidz-grant-days" />
          <Pick value={g.kind} onChange={v => setG({ ...g, kind: v })} options={[["promo", "Promo"], ["restore", "Restoration"]]} testid="admin-bidz-grant-kind" />
        </div>
        <Input placeholder="Reason (required)" value={g.reason} onChange={e => setG({ ...g, reason: e.target.value })} data-testid="admin-bidz-grant-reason" />
        <Button onClick={grant} disabled={!g.email || g.reason.length < 3} data-testid="admin-bidz-grant-submit" className="bg-slate-900 text-white">Grant Bidz</Button>
      </div>
      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 space-y-3" data-testid="admin-bidz-restore">
        <h3 className="font-bold">Restore an auction's Bidz (fraud / platform failure)</h3>
        <p className="text-xs text-slate-500">Returns every Bidz spent on the auction and replaces unused pass Bidz. Safe to run twice: nothing is restored twice. Removing a listing does this automatically.</p>
        <Input placeholder="Listing ID" value={r.listing_id} onChange={e => setR({ ...r, listing_id: e.target.value })} data-testid="admin-bidz-restore-listing" />
        <Input placeholder="Reason (required)" value={r.reason} onChange={e => setR({ ...r, reason: e.target.value })} data-testid="admin-bidz-restore-reason" />
        <Button onClick={restore} disabled={!r.listing_id || r.reason.length < 3} data-testid="admin-bidz-restore-submit" className="bg-red-600 hover:bg-red-500 text-white">Restore Bidz</Button>
      </div>
    </div>
  );
}

function Overview({ o, onRestoreTxn }) {
  const [q, setQ] = useState("");
  if (!o) return null;
  const rows = o.transactions.filter(t => !q || (t.user_email || "").toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-bidz-overview">
        {Object.entries(o.revenue).map(([k, v]) => { const [kind, c] = k.split("_"); return <Box key={k} k={`${kind === "pass" ? "Pass" : "Bidz"} revenue (${c})`} v={money(v, c)} />; })}
        <Box k="Active passes" v={o.active_passes} />
        <Box k="Outstanding Bidz" v={o.outstanding_bidz} />
        <Box k="Bidz sold" v={o.totals_by_type.purchase || 0} />
        <Box k="Bidz spent" v={-(o.totals_by_type.deduct || 0)} />
      </div>
      <Input placeholder="Filter transactions by user email…" value={q} onChange={e => setQ(e.target.value)} data-testid="admin-bidz-txn-filter" className="max-w-sm" />
      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 overflow-x-auto bg-white dark:bg-slate-900">
        <table className="w-full text-sm min-w-[760px]" data-testid="admin-bidz-txns">
          <thead className="bg-slate-50 dark:bg-slate-800 text-left text-xs uppercase text-slate-500"><tr><th className="px-4 py-3">When</th><th className="px-4 py-3">User</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Details</th><th className="px-4 py-3 text-right">Bidz</th><th className="px-4 py-3"></th></tr></thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
            {rows.map(t => (
              <tr key={t.id}>
                <td className="px-4 py-2 whitespace-nowrap">{new Date(t.created_at).toLocaleString("en-IN")}</td>
                <td className="px-4 py-2">{t.user_email || "—"}</td>
                <td className="px-4 py-2 font-semibold">{t.type}</td>
                <td className="px-4 py-2 text-slate-500">{t.reason}{t.listing_title ? ` · ${t.listing_title}` : ""}</td>
                <td className={`px-4 py-2 text-right font-bold ${t.bidz < 0 ? "text-red-600" : "text-emerald-600"}`}>{t.bidz}</td>
                <td className="px-4 py-2">{t.type === "deduct" && !t.restored && <Button size="sm" variant="outline" onClick={() => onRestoreTxn(t)} data-testid={`admin-bidz-restore-txn-${t.id}`}>Restore</Button>}{t.restored && <span className="text-xs text-slate-500">restored</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
function Box({ k, v }) {
  return <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4"><div className="text-[10px] uppercase font-bold text-slate-500">{k}</div><div className="text-xl font-extrabold mt-1">{v}</div></div>;
}

function ValueNote({ pkgs }) {
  if (!pkgs.length) return null;
  return (
    <div className="text-xs text-slate-500" data-testid="admin-bidz-value-note">
      Value per Bidz: {pkgs.filter(p => p.active).map(p => `${p.name} ${money(p.price / p.bidz)}`).join(" · ")}. The Auction Pass is a separate pay-per-auction product.
    </div>
  );
}

function RestoreTxnDialog({ txn, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const submit = async () => {
    try { await api.post(`/admin/bidz/restore-txn/${txn.id}`, { reason }); toast.success("Bidz restored"); setReason(""); onDone(); } catch (e) { err(e); }
  };
  return (
    <Dialog open={!!txn} onOpenChange={o => !o && onClose()}>
      <DialogContent data-testid="admin-bidz-restore-txn-dialog">
        <DialogHeader><DialogTitle>Restore {txn ? -txn.bidz : 0} Bidz to {txn?.user_email}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Use for fraud or a verified platform failure. Restored Bidz go to the buyer's wallet.</p>
        <Input placeholder="Reason (required)" value={reason} onChange={e => setReason(e.target.value)} data-testid="admin-bidz-restore-txn-reason" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={reason.trim().length < 3} className="bg-red-600 hover:bg-red-500 text-white" data-testid="admin-bidz-restore-txn-confirm">Restore Bidz</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const AdminBidz = () => {
  const [pkgs, setPkgs] = useState([]);
  const [o, setO] = useState(null);
  const [adding, setAdding] = useState(false);
  const [txn, setTxn] = useState(null);
  const load = () => {
    api.get("/admin/bidz/packages").then(r => setPkgs(r.data)).catch(err);
    api.get("/admin/bidz/overview").then(r => setO(r.data)).catch(err);
  };
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-8" data-testid="admin-bidz">
      <Overview o={o} onRestoreTxn={setTxn} />
      <RestoreTxnDialog txn={txn} onClose={() => setTxn(null)} onDone={() => { setTxn(null); load(); }} />
      <div className="space-y-3">
        <div className="flex items-center justify-between"><h3 className="text-lg font-bold">Packages & passes</h3>
          <Button size="sm" variant="outline" onClick={() => setAdding(true)} data-testid="admin-bidz-add-pkg"><Plus className="h-4 w-4 mr-1" /> New package</Button>
        </div>
        <ValueNote pkgs={pkgs} />
        {pkgs.map(p => <PackageRow key={`${p.id}-${p.updated_at || ""}`} p={p} onSaved={load} />)}
        {adding && <PackageRow p={BLANK} onSaved={() => { setAdding(false); load(); }} />}
      </div>
      <RulesCard />
      <ActionsCard onDone={load} />
    </div>
  );
};
